import { useQuery } from '@tanstack/react-query';
import { getCoveragePayloadSchemas } from '../api';
import { getData } from '../../../../../utils/functional/fp';
import type { CoveragePayloadSchemaMap } from '../api/types';

interface UseGetCoveragePayloadSchemasResult {
  readonly schemas: CoveragePayloadSchemaMap;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isEmpty: boolean;
}

const EMPTY_SCHEMAS: CoveragePayloadSchemaMap = {};

/**
 * Fetches the excess coverage payload schema map for a product + version, cached
 * forever (schemas are static per version). Decoupled from calculate/render: it
 * never blocks rating. Failure yields an empty map flagged via `isError`, kept
 * distinct from a legitimately-empty result (`isEmpty`) so the excess branch can
 * decide whether to show a degradation notice (FPX-6084).
 */
export function useGetCoveragePayloadSchemas(
  productKey: string,
  productVersion: number | undefined
): UseGetCoveragePayloadSchemasResult {
  const query = useQuery({
    queryKey: ['coverage-payload-schemas', { productKey, productVersion }],
    queryFn: () =>
      getCoveragePayloadSchemas(productKey, productVersion as number).then(
        getData
      ),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!productKey && productVersion != null,
  });

  const schemas = query.data?.schemas ?? EMPTY_SCHEMAS;

  return {
    schemas,
    isLoading: query.isLoading,
    isError: query.isError,
    isEmpty: query.isSuccess && Object.keys(schemas).length === 0,
  };
}
